import { postJSON } from 'common/services/api'
import { UseMutateFunction, useMutation, useQueryClient } from 'react-query'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { showToast } from 'common/utils'
import { useTranslation } from 'react-i18next'
import {
  ALLOCATION_LIST_QUERY_KEY,
  CLOCK_IN_QUERY_KEY,
  CLOCK_IN_URL,
  CLOCK_OUT_QUERY_KEY,
  CLOCK_OUT_URL,
  INSTANCE_QUERY_KEY,
  INSTANCES_QUERY_KEY,
  REVISION_QUERY_KEY,
  REVISIONS_QUERY_KEY,
  TODAY_MONITORING_QUERY_KEY,
} from './queryConstants'

dayjs.extend(isoWeek)

type UseClockInOutReturn = {
  clockInOut: UseMutateFunction<any, unknown, ClockInOutArgs, unknown>
}

export type ClockInOutType = 'in' | 'out'
export type ClockInOutAction = 'manual' | 'edit'

export type ClockInOutArgs = {
  dateTime: string
  action: ClockInOutAction
  teamMemberId?: string
  note?: string
  lat?: number
  lng?: number
  accuracy?: number
}

export type UseClockInOutProps = {
  revisionId: string
  type: ClockInOutType
}

const useClockInOut = ({
  revisionId,
  type,
}: UseClockInOutProps): UseClockInOutReturn => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const clockInOut = useMutation(
    [type === 'in' ? CLOCK_IN_QUERY_KEY : CLOCK_OUT_QUERY_KEY],
    ({ action, ...json }: ClockInOutArgs) => {
      const baseUrl = type === 'in' ? CLOCK_IN_URL : CLOCK_OUT_URL
      return postJSON({
        url: `/${baseUrl}/${revisionId}`,
        isRota: true,
        json,
        method: action === 'manual' ? 'POST' : 'PUT',
      })
    },
    {
      onSuccess: () => {
        showToast({
          type: 'success',
          title: t(`CLOCKED_${type.toUpperCase()}_SUCCESSFULLY`),
        })

        queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
        queryClient.invalidateQueries([REVISION_QUERY_KEY, revisionId])
        queryClient.invalidateQueries([
          type === 'in' ? CLOCK_IN_QUERY_KEY : CLOCK_OUT_QUERY_KEY,
        ])
        queryClient.invalidateQueries([TODAY_MONITORING_QUERY_KEY])
        queryClient.invalidateQueries([ALLOCATION_LIST_QUERY_KEY])
        queryClient.invalidateQueries([INSTANCE_QUERY_KEY])
        queryClient.invalidateQueries([INSTANCES_QUERY_KEY])
      },
      onError: () => {
        showToast({ type: 'error', title: t('SOMETHING_WENT_WRONG') })
      },
    }
  )

  return {
    clockInOut: clockInOut.mutate,
  }
}

export default useClockInOut
